import { createSelector } from '@ngrx/store';
import { PublicTimelineItem, PublicProgram, createPublicProgram } from 'globals/store/public.model';
import { getTwoDeepSet, getTimeZoneDetails } from 'src/app/helpers/helpers';
import { date2str, flatten, getDefaultTimeZoneDetails, getNestedSet, sortByProp, str2Date, makeMapFromListOfObjectsAndKeyToProperty,  setUnion, getSet, makeMapFromListOfObjectsAndKey, listsShareSomeElement, ArrayProperties } from 'globals/helpers/helpers';
import { FilterGroup, filterStateSelector } from '../filters.era';
import { JoiEvent, LabelObject, Venue } from 'globals/store/events.data';
import { FullLogisticsShareData, getLogisticsShareData } from 'globals/store/logistics.data';
import { PermissionAccess, permissionsStateSelector } from './../permissions.era';
import { getAllTagPanelData, getTagPanelData, TagPanelData } from 'globals/store/tags.data';
import { Dictionary } from '@ngrx/entity';
import { Program, VenueSelection, getLinkableVenues, TimeZoneDetails, Itinerary, DropdownOption, PROGRAM_NAMES, sortPrograms } from 'globals/store/programs.data'
import { WorkerWithProfile } from 'globals/store/workforce.data';
import { Performer, performersStateSelector } from '../performers.era';
import { Sponsor } from 'globals/store/sponsors.data';
import { Exhibitor } from 'globals/store/exhibitors.data';
import { filterPrograms } from 'globals/store/reselectors/filteredPrograms.reselector';
import { DAY_MS } from 'globals/helpers/constants';
import { findTimeZone, getUnixTime, getZonedTime } from 'timezone-support';
import { JoiResource } from 'globals/store/resources.data';
import { columnOrderTypes, settingsStateSelector } from 'src/app/store/settings.era';
import { allDefaultProgramColumns, programTableColumnsById } from '../../helpers/table-helpers';
import { TableColumn } from '../../helpers/table.data';
import { ProgramTableData } from '../../helpers/table-column-types';
import { Asset } from 'globals/store/assets.data';
import { getReadyAssetLights, onsiteActivityListReselector } from './entity.decircular.reselector';
import { LogisticsShareable } from 'globals/store/share.data';
import { OnsiteActivity, OnsiteProgram } from 'globals/store/onsite.data';
import { programsStateSelector } from '../programs.era';
import { eventStateSelector } from '../events.era';
import { workforceStateSelector } from '../workforce.era';
import { onsiteStateSelector } from '../onsite.era';
import { sponsorsStateSelector } from '../sponsors.era';
import { exhibitorsStateSelector } from '../exhibitors.era';
import { resourcesStateSelector } from '../resources.era';
import { assetsStateSelector } from '../assets.era';
import { shareStateSelector } from '../share.era';
import { attendeesStateSelector } from '../attendees.era';

const dateIdReselector = createSelector(onsiteStateSelector, (state) => state.selectedDateString)
const publishFilters = createSelector(filterStateSelector, (state) => state.publish)
const logisticsFilters = createSelector(filterStateSelector, (state) => state.programLogistics)
const programEntities = createSelector(programsStateSelector, (state) => state.entities ?? {})
const programIds = createSelector(programsStateSelector, (state) => state.ids ?? [])
const event = createSelector(eventStateSelector, (state) => state.event)
const programOrder = createSelector(eventStateSelector, (state) => state.programOrder ?? [])
const eventVenues = createSelector(eventStateSelector, (state) => state.event ? state.event.venues : null)
const accessPermissions = createSelector(permissionsStateSelector, (state) => state.access)
const performers = createSelector(performersStateSelector, (state) => state.entities)
const workersByEmail = createSelector(workforceStateSelector, (state) => state.workersByEmail)
const sponsors = createSelector(sponsorsStateSelector, (state) => state.entities)
const exhibitors = createSelector(exhibitorsStateSelector, (state) => state.entities)
const resources = createSelector(resourcesStateSelector, (state) => state.entities)
const userSettings = createSelector(settingsStateSelector, (state) => state.user)
const programTableFilters = createSelector(filterStateSelector, (state) => state.programTable)
const assets = createSelector(assetsStateSelector, (state) => state.entities)
const programLabels = createSelector(eventStateSelector, (state) => state.labelMaps.program_labels)
const logisticsShareables = createSelector(shareStateSelector, (state) => state.logistics)
const attendees = createSelector(attendeesStateSelector, (state) => state.entities)

export const venueSelectionsReselector = createSelector(
  event,
  (event: JoiEvent): VenueSelection[] => {

    const venueSelections: VenueSelection[] = getLinkableVenues(event).map(({ venue, address, id }) => ({ venue, address, displayedTitle: venue, id }))

    let previousSelection: VenueSelection
    venueSelections.forEach(selection => {
      const { venue, address } = selection      
      if (previousSelection && previousSelection.venue === venue && previousSelection.address !== address) {
        previousSelection.displayedTitle = `${venue} (${previousSelection.address})`
        selection.displayedTitle = `${venue} (${address})`
      }
      previousSelection = selection
    })
    return venueSelections
  }
)

export const formattedDateReselector = createSelector(
  programEntities,
  (programs, programId: string) => {
    const date = programs[programId]?.date
    return date ? new Date(date) : null
  }
)

export const programColumnsReselector = createSelector(
  userSettings,
  (settings) => {
    const selectedColumns = !settings ? null : settings[`${columnOrderTypes.programs}_columns`]
    return (!selectedColumns ? allDefaultProgramColumns : selectedColumns.filter(c => c.selected).map(c => programTableColumnsById[c.value])).filter((c: TableColumn) => !c.notOuter)
  }
)

export const programTagReselector = createSelector(
  programEntities,
  workersByEmail,
  performers,
  sponsors,
  exhibitors,
  resources,
  attendees,
  (programs: Dictionary<Program>, workersByEmail: { [email:string]: WorkerWithProfile }, performers: { [id:string]: Performer }, sponsors: { [id:string]: Sponsor }, exhibitors: { [id:string]: Exhibitor }, resources: Dictionary<JoiResource>, attendees, programId: string ): TagPanelData => {

    const program = programs[programId]
    if (!program) return null

    const { workforceTags, performerTags, exhibitorTags, sponsorTags, resourceTags, attendeeTags } = program
    return getTagPanelData(workforceTags, performerTags, sponsorTags, exhibitorTags, workersByEmail, performers, sponsors, exhibitors, resourceTags, resources, attendeeTags, attendees)
  }
)

export const programPermissionsReselector = createSelector(
  event,
  accessPermissions,
  (event: JoiEvent, access: PermissionAccess) => ({
    is_readonly: !event || event.closed || !access || (!access.types.includes('program') && !access.types.includes('logistics')),
    is_program: access && access.types.includes('program'),
    is_logistics: access && access.types.includes('logistics'),
  })
)

export const programsArray = createSelector(
  programOrder,
  programEntities,
  (programOrder, entities): Program[]  => {
    const allPrograms: (Program | OnsiteProgram)[] = Object.values(entities)
    return sortPrograms(programOrder, allPrograms) as Program[]
  }
)

export const fullProgramOrderReselector = createSelector(
  programsArray,
  (programs) => (programs || []).map(p => p.id)
)

export const programOptionsReselector= createSelector(
  programsArray,
  (programs: Program[], newOption: boolean): DropdownOption[]  => {
    const programOptions = programs.map(p => ({ id: p.id, title: p.title }))
    if (newOption) programOptions.push({ id: 'new', title: ` + Add a ${ PROGRAM_NAMES.btn }`})
    return programOptions
  }
)

const getProgramTableData = (program: Program, assetsMap: Dictionary<Asset>, programLabels: LabelObject, event: JoiEvent): ProgramTableData => {
  const { title, files, assetIds, setup, setup_items, labels, expectedNumber, location, venueId, timeZoneDetails, secondaryTimeZoneDetails } = program
  const venue = event.venues[venueId] 
  const timeZone = secondaryTimeZoneDetails || timeZoneDetails
  const { abbr, offsetText } = timeZone

  const programTableData: ProgramTableData = {
    program,
    id: program.id,
    name: { text: title },
    assets: getReadyAssetLights(assetIds, assetsMap),
    files: !files?.length ? null : { text: `${files.length}` },
    venueLocation: { text: location || '' },
    venueAddress: !venue ? null : { text: venue.address },
    timeZone: { text: `${abbr} (GMT ${offsetText})`},
    setup: { text: setup || '' },
    setupNotes: !setup_items?.length ? null : { text: `${setup_items.length}` },
    expectedNumbers: !expectedNumber ? null : { text: `${expectedNumber}` },
    labels: !labels ? [] : labels.filter(l => programLabels[l]).map(label => ({ color: programLabels[label].color, label: programLabels[label].label }))
  }
  return programTableData

}

export const SearchablProgramFields: (keyof Program)[] = ['title', 'location']

export const programTableReselector = createSelector(
  programsArray,
  programTableFilters,
  assets,
  programLabels,
  event,
  (programsList, programTableFilters, assets, programLabels, event) => {

    const { search, programLabels: labels, programs: programIds } = programTableFilters
    if (search) {
      const searchTerm = search.toLocaleLowerCase()
      programsList = programsList.filter(p => SearchablProgramFields.some(field => p[field] && p[field].toLocaleLowerCase().includes(searchTerm)))
    }
    if (programIds?.length) programsList = programsList.filter(p => programIds.includes(p.id))
    if (labels?.length) programsList = programsList.filter(p => listsShareSomeElement(p.labels, labels))

    const tableRows: ProgramTableData[] = programsList.map(p => getProgramTableData(p, assets, programLabels, event))
    const tableRowsById = makeMapFromListOfObjectsAndKey(tableRows, 'id')
    return { tableRows, tableRowsById }
  }
)

export const programSessionsActivitesMegaTagReselector = createSelector(
  programsArray,
  onsiteActivityListReselector,
  workersByEmail,
  performers,
  sponsors,
  exhibitors,
  resources,
  attendees,
  getAllTagPanelData
)

export const filteredLogisticsProgramsReselector = createSelector(
  programsArray,
  logisticsFilters,
  filterPrograms
)

export const filteredLogisticsShareReselector = createSelector(
  event,
  programsArray,
  logisticsShareables,

  (event: JoiEvent, programsArray, logisticsShareables: Dictionary<LogisticsShareable>, { code }: { code: string}): FullLogisticsShareData => {
    const share = logisticsShareables[code]
    return getLogisticsShareData(event, programsArray, share)
  }
)

export const itineraryReselector = createSelector(
  programsArray,
  event,
  (programs, event): Itinerary[] => flatten(programs.filter(p => p.date).map(p => p.sessions.map((s, k) => ({
    date: new Date(new Date(p.date).getTime() + k * 24 * 60 * 60 * 1000),
    time: s.start + ' - ' + s.end,
    title: p.title,
    location: p.location,
    venue: event?.venues[p.venueId]?.venue,
    address: event?.venues[p.venueId]?.address,
    expectedNumber: p.expectedNumber
  })))).sort((d1, d2) => d1.date.getTime() - d2.date.getTime())
)

export const getListOfTimezonesUsed = (timeZoneIds: Set<string>, date: Date): TimeZoneDetails[] => {
  const details = Array.from(timeZoneIds).map(timeZoneId => getTimeZoneDetails(timeZoneId, date)).sort(sortByProp('abbr'))
  // console.log({ timeZoneIds, details })
  return details
}
 

export const getProgramTimezoneIds = createSelector(
  programsArray,
  (programs: Program[]) => setUnion(getTwoDeepSet(programs, 'timeZoneDetails', 'timeZoneId'), getTwoDeepSet(programs, 'secondaryTimeZoneDetails', 'timeZoneId'))
)

export const getAllTimezoneIds = createSelector(
  getProgramTimezoneIds,
  event,
  (programTimezoneIds, event) => !event ? new Set() as Set<string> : setUnion(getSet<any, string>(Object.values(event.venues), 'timeZoneId'), programTimezoneIds) as Set<string>
)

export const getProgramListOfTimeZonesUsed = createSelector(
  programEntities,
  getAllTimezoneIds,
  (programs: Dictionary<Program>, timezoneIds: Set<string>, programId: string) => getListOfTimezonesUsed(timezoneIds, str2Date(programs[programId].date || null))
)


export const getOSSListOfTimeZonesUsed = createSelector(
  getAllTimezoneIds,
  dateIdReselector,
  (timezoneIds: Set<string>, dateId: string) => {
    const date = str2Date(dateId)
    const listOfTimeZonesUsed = getListOfTimezonesUsed(timezoneIds, date)
    return { timeZoneAbbrs: makeMapFromListOfObjectsAndKeyToProperty(listOfTimeZonesUsed, 'timeZoneId', 'abbr'), listOfTimeZonesUsed }
  }
)



export const missingOutdatedVenueFormatVenues = createSelector(
  programEntities,
  eventVenues,
  (programs: Dictionary<Program>, venues:{[venueId:string]:Venue}):Venue[]=>{
    if(!venues) return [];
    return [...new Set( Object.values(programs)
      .filter(p=>(!p.venueId || !venues[p.venueId] || !venues[p.venueId].googleId) && (p['venue'] || p['address']))
      .map(p=>({ id:p.venueId, venue:p['venue'], address:p['address'], latitude:null, longitude:null, timeZoneId: getDefaultTimeZoneDetails().timeZoneId } as Venue)))]
  }
)

export const publicPublishTimelineReselector = createSelector(
  programIds,
  programEntities,
  publishFilters,
  fullProgramOrderReselector,
  (ids:string[], entities, filters: FilterGroup, fullProgramOrder: string[]): { timeline: PublicTimelineItem[][], prefilteredPerformerIds: Set<string> } =>{
    let sessions: PublicTimelineItem[] = []
    const sessionIds = [];
    const sessionEntities: {[key:string]:PublicTimelineItem} = {};
    const programEntities: {[key:string]:PublicProgram}  = {}

    const allSessions = flatten(Object.values(entities).map((e: any) => Object.values(e.sessions_obj)))
    const prefilteredPerformerIds: Set<string> = getNestedSet<any, string, ArrayProperties<any, any>>(allSessions, 'performerIds') // these are necessary for the performer gallery

    ids.forEach((pid)=>{
      // if(!entities[pid].programConfirmed) return;
      const { date } = entities[pid]
      const program: PublicProgram = createPublicProgram(entities[pid])
      programEntities[pid] = program;
      const prog_date = date ? new Date(date) : null
      const sessions_obj = entities[pid].sessions_obj;
      const timeZoneId = entities[pid].timeZoneDetails?.timeZoneId || getDefaultTimeZoneDetails().timeZoneId
      const timeZone = findTimeZone(timeZoneId)
      // console.log({ timeZoneId, timeZone, date })
      window['findTimeZone'] = findTimeZone
      window['getZonedTime'] = getZonedTime
      window['getUnixTime'] = getUnixTime
      window['timeZone'] = timeZone

      entities[pid].sessions.forEach(sessionDay => {

        /*

        const sessionDay = new Date((new Date(entities[pid].date).getTime()) + (+day.day - 1) * DAY_MS)
        const usersTimezoneOffset = (-1) * sessionDay.getTimezoneOffset() * 60 * 1000;
        const programEpoch = sessionDay.getTime() + usersTimezoneOffset * 2; // Why * 2? => Once is to get to UTC, twice is to compensate for js autocorrecting that happens on the browser
        const timezoneDetails = getZonedTime(new Date(programEpoch), timeZone);
        const timezoneOffset = timezoneDetails.zone.offset * 60 * 1000;
        const timezoneAbbr = timezoneDetails.zone.abbreviation;

        */

        sessions = [...sessions, ...sessionDay.sessions.filter(sid=> {
            const { type, tags, groupTags, hideInPublish } = sessions_obj[sid]
            return ![ // gotta move tag filtering here, as the data is not available to do so later
              !hideInPublish,
              type !== 'break',
              !filters.tags ||  (tags && tags.some(t => filters.tags.includes(t))),
              !filters.groupTags ||  (groupTags && groupTags.some(t => filters.groupTags.includes(t))),
            ].some(passesFilter => !passesFilter)
          }).map(sid=>{
            const { duration, title:session_title, on_day, start, end, description, labels:session_labels, performerIds, sponsorIds, publicLinks, performerRoles } = entities[pid].sessions_obj[sid]
                        
            let timezoneAbbr: string = null
            let epoch: number = null
            if (date) {
              const sessionDate = new Date((new Date(date).getTime()) + (+sessionDay.day - 1) * DAY_MS)
              const sessionDateString = date2str(sessionDate)
              const [ year, month, day ] = sessionDateString.split("/")
              const [ hours, minutes ] = start.split(":")
              const time = { year: +year, month: +month, day: + day, hours: +hours, minutes: +minutes }
              // console.log({ time, sessionDateString })
              epoch = getUnixTime(time, timeZone)
              const zonedTime = getZonedTime(epoch, timeZone)
              timezoneAbbr = zonedTime.zone.abbreviation
              // console.log({ epoch, zonedTime, timezoneAbbr })
            }

            const session: PublicTimelineItem = { 
              performerRoleIds: performerRoles,
              id:sid, 
              title:session_title, 
              duration, 
              on_day, 
              start, 
              timezoneAbbr,
              timeZoneId,
              epoch,
              end, 
              description, 
              labels:session_labels, 
              performerIds, 
              sponsorIds,
              publicLinks,
              programId: pid
            };
            const session_date = prog_date ? date2str(new Date(new Date(prog_date).setDate(prog_date.getDate() + ((session.on_day - 1) ))) ): session.on_day;
            sessionIds.push(sid)
            sessionEntities[sid] = session;
            return {
              ...session,
              date,
              session_date,
              datetime: session_date.toString() + " " + session.start,
              program
            }
        })]
      })
    })

    const sorted = sessions.sort((a,b)=> {
      if (a.datetime !== b.datetime) return a.datetime > b.datetime ? 1 : -1
      if (a.programId === b.programId) return -1
      const aIndex = fullProgramOrder.findIndex(id => id === a.programId)
      const bIndex = fullProgramOrder.findIndex(id => id === b.programId)
      return aIndex - bIndex
    })

    const timeline: PublicTimelineItem[][] = []
    
    if (sorted.length) {
      let date = null
      let dayTimeline = null
      sorted.forEach(s => {
        const sDate = s.session_date.toString()
        if (date !== sDate) {
          date = sDate
          dayTimeline = []
          timeline.push(dayTimeline)
        }
        dayTimeline.push(s)
      })       
    }
    return { timeline, prefilteredPerformerIds }
  }
)



